/** Cross-tenant strategy chooser for a batch op that applies a tenant-scoped
 *  value (a launch policy, a tag, …) to a selection spanning multiple tenants.
 *
 *  The server is authoritative — it re-detects the cross-tenant condition and
 *  rejects a missing/unsupported strategy with 400 `{cross_tenant,
 *  allowed_strategies, capabilities}`. This field only renders the strategies the
 *  server advertised (`allowedStrategies`) so the operator can pick one before
 *  resubmitting. `noun` parameterises the human labels (e.g. "launch policy").
 */
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';

interface Props {
  value: string | null;
  onChange: (s: string) => void;
  allowedStrategies: string[];
  noun?: string;
}

/** Human labels per strategy key, parameterised by the value's noun. */
function strategyLabel(strategy: string, noun: string): string {
  switch (strategy) {
    case 'ignore_others':
      return `Only apply to records in the ${noun}'s tenant (skip the rest)`;
    case 'match_or_ignore':
      return `Use the matching ${noun} in each tenant where it exists (skip where missing)`;
    case 'match_or_create':
      return `Use or create the matching ${noun} in each tenant`;
    default:
      return strategy;
  }
}

export function CrossTenantStrategyField({
  value,
  onChange,
  allowedStrategies,
  noun = 'value',
}: Props) {
  return (
    <FormControl>
      <FormLabel sx={{ fontSize: 14 }}>This selection spans multiple tenants</FormLabel>
      <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {allowedStrategies.map((s) => (
          <FormControlLabel
            key={s}
            value={s}
            control={<Radio size="small" />}
            label={strategyLabel(s, noun)}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}
